import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import './EditCampaignModal.css';

/**
 * CreativeScheduleModal - Modal for creating/editing creative schedules
 * 
 * Features:
 * - Ad ID input (for manual creation)
 * - Start date and end date configuration
 * - Notes field
 */
const CreativeScheduleModal = ({ 
  isOpen, 
  onClose, 
  campaign, 
  schedule = null, // null for create, object for edit
  onSubmit, 
  isSubmitting 
}) => {
  // Form state
  const [adId, setAdId] = useState('');
  const [adName, setAdName] = useState('');
  const [adsetId, setAdsetId] = useState('');
  const [adsetName, setAdsetName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('00:00');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('23:59');
  const [timezone, setTimezone] = useState('America/Los_Angeles');
  const [notes, setNotes] = useState('');

  // Timezone options
  const timezones = [
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'UTC', label: 'UTC' },
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' }
  ];

  // Get today's date in YYYY-MM-DD format for min date validation (using local time, not UTC)
  const today = (() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  })();

  // Get current time in HH:MM format
  const getCurrentTime = () => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  };

  // Calculate minimum time for a date (only apply if date is today)
  const getMinTime = (selectedDate) => {
    if (selectedDate === today) {
      return getCurrentTime();
    }
    return '00:00';
  };

  // Populate form when editing
  useEffect(() => {
    if (schedule) {
      setAdId(schedule.adId || '');
      setAdName(schedule.adName || '');
      setAdsetId(schedule.adsetId || '');
      setAdsetName(schedule.adsetName || '');
      setStartDate(schedule.startDate?.split('T')[0] || '');
      setStartTime(schedule.startTime?.substring(0, 5) || '00:00');
      setEndDate(schedule.endDate?.split('T')[0] || '');
      setEndTime(schedule.endTime?.substring(0, 5) || '23:59');
      setTimezone(schedule.timezone || 'America/Los_Angeles');
      setNotes(schedule.notes || '');
    } else {
      // Reset form for new schedule
      setAdId('');
      setAdName('');
      setAdsetId('');
      setAdsetName('');
      setStartDate('');
      setStartTime('00:00');
      setEndDate('');
      setEndTime('23:59');
      setTimezone('America/Los_Angeles');
      setNotes('');
    }
  }, [schedule, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();

    const scheduleData = {
      adId: adId.trim(),
      adName: adName.trim() || null,
      adsetId: adsetId.trim() || null,
      adsetName: adsetName.trim() || null,
      startDate: startDate || null,
      startTime: startTime ? `${startTime}:00` : null,
      endDate: endDate || null,
      endTime: endTime ? `${endTime}:00` : null,
      timezone,
      notes: notes.trim() || null
    };

    onSubmit(scheduleData);
  };

  if (!isOpen) return null;

  const isEditing = !!schedule;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '8px', color: '#10b981' }} />
            {isEditing ? 'Edit Creative Schedule' : 'Add Creative Schedule'}
          </h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Ad Information */}
            <div className="form-section">
              <h3>Ad Information</h3>
              
              <div className="form-group">
                <label htmlFor="adId">Ad ID *</label>
                <input
                  type="text"
                  id="adId"
                  value={adId}
                  onChange={(e) => setAdId(e.target.value)}
                  placeholder="e.g., 120241806089470437"
                  required
                  disabled={isEditing}
                />
                {isEditing && (
                  <small className="form-help">Ad ID cannot be changed after creation</small>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="adName">Ad Name</label>
                <input
                  type="text"
                  id="adName"
                  value={adName}
                  onChange={(e) => setAdName(e.target.value)}
                  placeholder="Optional: Ad display name"
                  disabled={isEditing}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="adsetId">AdSet ID</label>
                  <input
                    type="text"
                    id="adsetId"
                    value={adsetId}
                    onChange={(e) => setAdsetId(e.target.value)}
                    placeholder="Optional"
                    disabled={isEditing}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="adsetName">AdSet Name</label>
                  <input
                    type="text"
                    id="adsetName"
                    value={adsetName}
                    onChange={(e) => setAdsetName(e.target.value)}
                    placeholder="Optional"
                    disabled={isEditing}
                  />
                </div>
              </div>
            </div>

            {/* Schedule Dates */}
            <div className="form-section">
              <h3>Schedule Dates & Times</h3>
              <p className="section-description">
                Set when the ad should be enabled (start) and disabled (end)
              </p>

              {/* Start Date & Time */}
              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label htmlFor="startDate">Start Date (Enable)</label>
                  <input
                    type="date"
                    id="startDate"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    min={today}
                    disabled={schedule?.startStatus === 'completed' || schedule?.startStatus === 'processing'}
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="startTime">Time</label>
                  <input
                    type="time"
                    id="startTime"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    min={getMinTime(startDate)}
                    disabled={schedule?.startStatus === 'completed' || schedule?.startStatus === 'processing'}
                  />
                </div>
              </div>
              {(schedule?.startStatus === 'completed' || schedule?.startStatus === 'processing') ? (
                <small className="form-help" style={{ marginTop: '-8px', marginBottom: '12px', display: 'block' }}>
                  Already {schedule.startStatus} - cannot edit
                </small>
              ) : (
                <small className="form-help" style={{ marginTop: '-8px', marginBottom: '12px', display: 'block' }}>
                  Ad will be set to ACTIVE at this date/time
                </small>
              )}

              {/* End Date & Time */}
              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label htmlFor="endDate">End Date (Disable)</label>
                  <input
                    type="date"
                    id="endDate"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate || today}
                    disabled={schedule?.endStatus === 'completed' || schedule?.endStatus === 'processing'}
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="endTime">Time</label>
                  <input
                    type="time"
                    id="endTime"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    min={endDate === today ? getMinTime(endDate) : '00:00'}
                    disabled={schedule?.endStatus === 'completed' || schedule?.endStatus === 'processing'}
                  />
                </div>
              </div>
              {(schedule?.endStatus === 'completed' || schedule?.endStatus === 'processing') ? (
                <small className="form-help" style={{ marginTop: '-8px', marginBottom: '12px', display: 'block' }}>
                  Already {schedule.endStatus} - cannot edit
                </small>
              ) : (
                <small className="form-help" style={{ marginTop: '-8px', marginBottom: '12px', display: 'block' }}>
                  Ad will be set to PAUSED at this date/time
                </small>
              )}

              {/* Timezone */}
              <div className="form-group">
                <label htmlFor="timezone">Timezone</label>
                <select
                  id="timezone"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="form-control"
                >
                  {timezones.map(tz => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Notes */}
            <div className="form-section">
              <div className="form-group">
                <label htmlFor="notes">Notes</label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional notes about this schedule"
                  rows={3}
                />
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting || (!startDate && !endDate) || (!isEditing && !adId.trim())}
            >
              {isSubmitting ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Saving...
                </>
              ) : (
                isEditing ? 'Update Schedule' : 'Create Schedule'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreativeScheduleModal;

